import { useState } from "react";
import type { FormEvent } from "react";
import PageHeader from "@/components/base/PageHeader";
import { contactInfo } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db7016mcdv04m4p4ge70";

const contactCards = [
  { icon: "ri-phone-line", label: "联系电话", value: contactInfo.phone },
  { icon: "ri-mail-line", label: "电子邮箱", value: contactInfo.email },
  { icon: "ri-map-pin-line", label: "办公地址", value: contactInfo.address },
  { icon: "ri-time-line", label: "服务时间", value: contactInfo.hours },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMessage === "string" && serverMessage.length > 0
            ? serverMessage
            : "提交失败，请稍后重试。",
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <PageHeader
        breadcrumb={[{ label: "联系我们" }]}
        eyebrow="联系方式"
        title="联系我们"
        description="无论是合作咨询、资料申请还是信息反馈，欢迎通过以下方式与我们取得联系，或直接提交在线反馈。"
      />

      <section className="px-4 md:px-8 py-14 md:py-20">
        <div className="mx-auto max-w-6xl grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr]">
          <div className="flex flex-col gap-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contactCards.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <span className="text-xs text-foreground-500">{item.label}</span>
                  <span className="text-sm font-medium text-foreground-900">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200">
              <iframe
                title="想要XX 办公地址地图"
                src="https://www.google.com/maps?q=Shanghai+Pudong+Century+Avenue&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              在线反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              填写以下信息，我们会在服务时间内尽快回复你。带 * 为必填项。
            </p>

            {status === "success" ? (
              <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-dashed border-accent-300 bg-accent-50/60 px-6 py-12 text-center">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-accent-500/15 text-accent-600">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  提交成功
                </h3>
                <p className="max-w-sm text-sm text-foreground-500">
                  感谢你的反馈，我们已收到你的留言，将尽快与你联系。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-2 inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
                >
                  再提交一条
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
                    姓名 *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入常用邮箱"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-700">
                    咨询主题
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="合作咨询"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="合作咨询">合作咨询</option>
                    <option value="资料申请">资料申请</option>
                    <option value="信息反馈">信息反馈</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
                    留言内容 *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请简要描述你的需求或问题（最多 500 字）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <input
                  className="hp-field-company-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-700">
                    <i className="ri-error-warning-line mr-2" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>
                <p className="text-xs text-foreground-400">
                  提交即表示你同意我们通过所留联系方式与你取得联系。
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}